import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button, Result } from 'antd'
import i18n from '@/i18n'
import { detectRuntime } from '@/store/useRuntimeStore'

interface Props {
  children: ReactNode
  /** 可选：自定义重置回调（默认整页刷新） */
  onReset?: () => void
}

interface State {
  error: Error | null
}

/**
 * 捕获子树渲染错误，避免整页白屏。
 * 放在 AntdProvider 内以便 Result / Button 有主题。
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  private handleReset = () => {
    if (this.props.onReset) {
      this.props.onReset()
      this.setState({ error: null })
      return
    }
    // 客户端内嵌走 hash 路由，不能跳宿主路径
    if (detectRuntime() === 'client') {
      window.location.hash = '#/apps'
      window.location.reload()
      return
    }
    window.location.assign('/apps')
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex min-h-full min-h-[100vh] items-center justify-center bg-[var(--page)] p-6">
        <Result
          status="error"
          title={i18n.t('errorBoundary.title')}
          subTitle={i18n.t('errorBoundary.desc')}
          extra={
            <Button type="primary" onClick={this.handleReset}>
              {i18n.t('errorBoundary.retry')}
            </Button>
          }
        />
      </div>
    )
  }
}

export default ErrorBoundary
